import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id: '', budget_amount: '', alert_threshold_percent: 80, email_notify: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const fetchBudgets = () =>
    api.get('/billing/budgets').then(({ data }) => setBudgets(data.budgets || [])).catch(() => {});

  const fetchProjects = () =>
    api.get('/billing/cost-overview').then(({ data }) => setProjects(data.projects || [])).catch(() => {});

  useEffect(() => { fetchBudgets(); fetchProjects(); }, []);

  const handleSave = async () => {
    if (!form.project_id || !form.budget_amount) {
      setMessage({ text: 'Project and budget amount are required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/billing/budgets', {
        ...form,
        budget_amount: parseFloat(form.budget_amount),
        alert_threshold_percent: parseInt(form.alert_threshold_percent),
      });
      setMessage({ text: 'Budget alert created successfully', type: 'success' });
      setShowModal(false);
      setForm({ project_id: '', budget_amount: '', alert_threshold_percent: 80, email_notify: '' });
      fetchBudgets();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to save', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getSpendPercent = (b) => Math.min(b.project_id.charCodeAt(0) % 100, 95);
  const getSpendColor = (pct, threshold) => {
    if (pct >= threshold) return '#EA4335';
    if (pct >= threshold * 0.9) return '#FF7043';
    if (pct >= threshold * 0.75) return '#FBBC05';
    return '#34A853';
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Budgets & Alerts</h1>
            <p style={s.subtitle}>Set spending limits and get notified when thresholds are crossed</p>
          </div>
          <button style={s.primaryBtn} onClick={() => { setShowModal(true); setMessage({ text:'',type:'' }); }}>+ Create Budget</button>
        </div>

        {message.text && (
          <div style={{ ...s.msgBox, background: message.type==='error' ? 'rgba(239,68,68,0.08)' : 'rgba(74,222,128,0.08)', borderColor: message.type==='error' ? 'rgba(239,68,68,0.3)' : 'rgba(74,222,128,0.3)', color: message.type==='error' ? '#fca5a5' : '#4ade80' }}>
            {message.type==='error' ? '⚠ ' : '✓ '}{message.text}
            <button onClick={() => setMessage({text:'',type:''})} style={s.closeBtn}>✕</button>
          </div>
        )}

        <div style={s.statsGrid}>
          {[
            { icon:'🎯', label:'Total Budgets', value: budgets.length, color:'#4285F4' },
            { icon:'⚠️', label:'Near Threshold', value: budgets.filter(b => getSpendPercent(b) >= b.alert_threshold_percent*0.9).length, color:'#FBBC05' },
            { icon:'🔴', label:'Over Budget', value: budgets.filter(b => getSpendPercent(b) >= b.alert_threshold_percent).length, color:'#EA4335' },
            { icon:'✅', label:'Within Budget', value: budgets.filter(b => getSpendPercent(b) < b.alert_threshold_percent*0.75).length, color:'#34A853' },
          ].map((c,i) => (
            <div key={i} style={s.statCard}>
              <div style={{ ...s.statIcon, background: c.color+'18', border: `1px solid ${c.color}28` }}>{c.icon}</div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          ))}
        </div>

        {budgets.length === 0 ? (
          <div style={s.emptyState}>
            <div style={{ fontSize:56, marginBottom:16 }}>🎯</div>
            <div style={{ color:'#1e293b', fontSize:18, fontWeight:600, marginBottom:8 }}>No budgets configured</div>
            <div style={{ color:'#94a3b8', marginBottom:24 }}>Create a budget alert to monitor your GCP spending</div>
            <button style={s.primaryBtn} onClick={() => setShowModal(true)}>+ Create First Budget</button>
          </div>
        ) : (
          <div style={s.budgetsGrid}>
            {budgets.map((b,i) => {
              const pct = getSpendPercent(b);
              const color = getSpendColor(pct, b.alert_threshold_percent);
              const isAlert = pct >= b.alert_threshold_percent;
              return (
                <div key={i} style={{ ...s.budgetCard, border: isAlert ? '1px solid rgba(234,67,53,0.4)' : '1px solid rgba(255,255,255,0.07)' }}>
                  {isAlert && <div style={s.alertBanner}>⚠ Threshold exceeded</div>}
                  <div style={s.budgetHeader}>
                    <code style={s.projectTag}>{b.project_id}</code>
                    <span style={s.thresholdBadge}>⚡ {b.alert_threshold_percent}% alert</span>
                  </div>
                  <div style={s.budgetAmount}>${Number(b.budget_amount).toLocaleString()} <span style={s.budgetSub}>USD / month</span></div>
                  <div style={s.progressTrack}>
                    <div style={{ ...s.progressFill, width:`${Math.min(pct,100)}%`, background:`linear-gradient(90deg,#34A853,${color})` }} />
                    <div style={{ position:'absolute', top:-4, bottom:-4, left:`${b.alert_threshold_percent}%`, width:2, background:'#FBBC05', borderRadius:2 }} />
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:16 }}>
                    <span style={{ color, fontWeight:600 }}>{pct}% used</span>
                    <span style={{ color:'#94a3b8' }}>${(Number(b.budget_amount)*pct/100).toLocaleString()} / ${Number(b.budget_amount).toLocaleString()}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      {b.email_notify && <div style={{ color:'#94a3b8', fontSize:12, marginBottom:2 }}>📧 {b.email_notify}</div>}
                      <div style={{ color:'#94a3b8', fontSize:11 }}>By {b.created_by_name} · {new Date(b.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {budgets.length > 0 && (
          <div style={s.noteCard}>
            <span>💡</span>
            <div style={{ color:'#94a3b8', fontSize:13, lineHeight:1.6 }}>
              <strong style={{ color:'#93c5fd' }}>Note: </strong>
              Spend percentages are illustrative. Connect BigQuery Billing Export for real-time spend tracking.
            </div>
          </div>
        )}

        {showModal && (
          <div style={s.overlay}>
            <div style={s.modal}>
              <div style={s.modalHeader}>
                <h3 style={s.modalTitle}>Create Budget Alert</h3>
                <button style={s.closeBtn} onClick={() => setShowModal(false)}>✕</button>
              </div>

              <div style={s.field}>
                <label style={s.label}>GCP Project</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} style={s.input}>
                  <option value="">Select a project...</option>
                  {projects.map(p => (
                    <option key={p.projectId} value={p.projectId}>{p.projectId} {p.billingEnabled ? '(Billing On)' : '(Billing Off)'}</option>
                  ))}
                </select>
              </div>

              <div style={s.field}>
                <label style={s.label}>Monthly Budget (USD)</label>
                <input type="number" value={form.budget_amount} onChange={e => setForm({...form, budget_amount: e.target.value})} placeholder="5000" style={s.input} />
              </div>

              <div style={s.field}>
                <label style={s.label}>Alert Threshold: <strong style={{ color:'#fbbf24' }}>{form.alert_threshold_percent}%</strong></label>
                <input type="range" min="50" max="100" step="5" value={form.alert_threshold_percent} onChange={e => setForm({...form, alert_threshold_percent: parseInt(e.target.value)})} style={{ width:'100%', accentColor:'#FBBC05', marginTop:8 }} />
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                  {[50,75,90,100].map(v => <span key={v} style={{ fontSize:11, color: v<=form.alert_threshold_percent ? '#fbbf24' : '#334155', fontWeight:600 }}>{v}%</span>)}
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Alert Email (optional)</label>
                <input type="email" value={form.email_notify} onChange={e => setForm({...form, email_notify: e.target.value})} placeholder="alerts@company.com" style={s.input} />
              </div>

              {form.project_id && form.budget_amount && (
                <div style={s.previewBox}>
                  <div style={{ color:'#94a3b8', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Preview</div>
                  <div style={{ color:'#1e293b', fontSize:13 }}>
                    Alert when <code style={s.code}>{form.project_id}</code> exceeds{' '}
                    <strong style={{ color:'#fbbf24' }}>{form.alert_threshold_percent}%</strong> of{' '}
                    <strong style={{ color:'#4ade80' }}>${Number(form.budget_amount||0).toLocaleString()}</strong>
                    {form.email_notify && <span> → notify <code style={s.code}>{form.email_notify}</code></span>}
                  </div>
                </div>
              )}

              <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:24 }}>
                <button style={s.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                <button style={s.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Create Budget Alert'}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  layout: { display:'flex', background:'#f5f7fa', minHeight:'100vh', fontFamily:"'Inter','Segoe UI',sans-serif" },
  main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title: { margin:0, color:'#0f172a', fontSize:26, fontWeight:700, letterSpacing:'-0.5px' },
  subtitle: { margin:'4px 0 0', color:'#64748b', fontSize:13 },
  primaryBtn: { background:'linear-gradient(135deg,#4285F4,#1a73e8)', border:'none', color:'#fff', borderRadius:8, padding:'10px 20px', cursor:'pointer', fontSize:13, fontWeight:600 },
  msgBox: { padding:'12px 16px', borderRadius:10, border:'1px solid', marginBottom:20, fontSize:14, display:'flex', justifyContent:'space-between', alignItems:'center' },
  closeBtn: { background:'none', border:'none', color:'#94a3b8', cursor:'pointer', fontSize:16, padding:'0 4px' },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 },
  statCard: { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:14, padding:'20px 22px' },
  statIcon: { width:40, height:40, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, marginBottom:14 },
  statValue: { color:'#0f172a', fontSize:28, fontWeight:700, letterSpacing:'-0.5px', marginBottom:4 },
  statLabel: { color:'#94a3b8', fontSize:13 },
  emptyState: { textAlign:'center', padding:'60px 20px', background:'#f8fafc', border:'1px solid #f1f5f9', borderRadius:16 },
  budgetsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:16, marginBottom:20 },
  budgetCard: { background:'#ffffff', borderRadius:14, padding:'22px' },
  alertBanner: { background:'rgba(234,67,53,0.15)', border:'1px solid rgba(234,67,53,0.3)', color:'#f87171', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, marginBottom:14, textAlign:'center' },
  budgetHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 },
  projectTag: { background:'rgba(255,255,255,0.07)', color:'#94a3b8', padding:'4px 10px', borderRadius:6, fontSize:12, fontFamily:'monospace' },
  thresholdBadge: { background:'rgba(251,191,36,0.1)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.2)', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 },
  budgetAmount: { color:'#0f172a', fontSize:28, fontWeight:700, letterSpacing:'-0.5px', marginBottom:16 },
  budgetSub: { color:'#64748b', fontSize:13, fontWeight:400 },
  progressTrack: { height:8, background:'rgba(255,255,255,0.08)', borderRadius:4, overflow:'visible', position:'relative', marginBottom:8 },
  progressFill: { height:'100%', borderRadius:4, transition:'width 0.5s' },
  noteCard: { display:'flex', gap:14, background:'rgba(66,133,244,0.06)', border:'1px solid rgba(66,133,244,0.15)', borderRadius:12, padding:'14px 18px', alignItems:'flex-start' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modal: { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:16, padding:'32px', width:'100%', maxWidth:500, maxHeight:'90vh', overflowY:'auto' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 },
  modalTitle: { margin:0, color:'#0f172a', fontSize:20, fontWeight:700 },
  field: { marginBottom:20 },
  label: { display:'block', color:'#94a3b8', fontSize:12, fontWeight:600, letterSpacing:'0.3px', textTransform:'uppercase', marginBottom:8 },
  input: { width:'100%', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 14px', color:'#0f172a', fontSize:14, outline:'none', boxSizing:'border-box' },
  previewBox: { background:'#ffffff', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'14px 16px' },
  code: { background:'rgba(255,255,255,0.08)', padding:'1px 6px', borderRadius:4, fontFamily:'monospace', fontSize:12, color:'#94a3b8' },
  cancelBtn: { background:'#f8fafc', border:'1px solid #e2e8f0', color:'#94a3b8', borderRadius:8, padding:'10px 20px', cursor:'pointer', fontSize:13 },
};